import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { AlertTriangle, Calendar, Clock, AlertCircle } from "lucide-react";

const AutoSchedulerWarningsDialog = ({ open, onOpenChange, result }) => {
  if (!result) return null;

  const { assignmentsMade, warnings = [], unassignedCount = 0 } = result;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Calendar className="h-6 w-6" />
            Auto-Scheduler Results
          </DialogTitle>
          <DialogDescription>
            Review the scheduling results and any conflicts
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* IMPORTANT DISCLAIMER */}
          <div className="p-5 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-700 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-yellow-900 mb-2 text-lg">⚠️ Important: Please Review Schedule</h3>
                <p className="text-sm text-yellow-900 mb-3">
                  The auto-scheduler uses AI to optimize assignments based on teamMember availability, asset compatibility, 
                  and scheduling preferences. However, it may not account for all real-world factors.
                </p>
                <div className="bg-yellow-100 p-3 rounded border border-yellow-300">
                  <p className="text-xs text-yellow-900 font-semibold mb-1">YOU MUST REVIEW AND VERIFY:</p>
                  <ul className="text-xs text-yellow-900 space-y-1 ml-4 list-disc">
                    <li>TeamMember certifications and experience level match trip requirements</li>
                    <li>Asset capacity and type are appropriate for each trip</li>
                    <li>No scheduling conflicts or overlapping assignments</li>
                    <li>Weather conditions and any last-minute changes</li>
                    <li>Customer special requests or accessibility needs</li>
                  </ul>
                </div>
                <p className="text-xs text-yellow-800 mt-3 font-medium">
                  ✓ The auto-scheduler is a powerful tool, but you are the final authority. Always double-check assignments before trips begin.
                </p>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700 font-medium">Successfully Assigned</p>
                  <p className="text-3xl font-bold text-green-900">{assignmentsMade}</p>
                </div>
                <div className="text-green-600">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-700 font-medium">Unassigned Bookings</p>
                  <p className="text-3xl font-bold text-orange-900">{unassignedCount}</p>
                </div>
                <div className="text-orange-600">
                  <AlertTriangle className="w-12 h-12" />
                </div>
              </div>
            </div>
          </div>

          {/* Warnings Section */}
          {warnings.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-600" />
                <h3 className="font-semibold text-lg">Scheduling Issues</h3>
              </div>

              <div className="space-y-3">
                {warnings.map((warning, index) => (
                  <div
                    key={index}
                    className="p-4 border border-orange-200 rounded-lg bg-orange-50 hover:bg-orange-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge variant="outline" className="bg-white">
                            <Calendar className="w-3 h-3 mr-1" />
                            {warning.date}
                          </Badge>
                          <Badge variant="outline" className="bg-white">
                            <Clock className="w-3 h-3 mr-1" />
                            {warning.startTime}
                          </Badge>
                          {warning.duration && (
                            <Badge variant="secondary">
                              {warning.duration} min
                            </Badge>
                          )}
                        </div>

                        <div className="space-y-1">
                          {warning.reasons.map((reason, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <div className="mt-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-orange-600" />
                              </div>
                              <p className="text-sm text-orange-900">{reason}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Suggestions */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">💡 Suggestions</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Check teamMember availability and unavailable dates</li>
                  <li>• Verify double-decker certifications if needed</li>
                  <li>• Ensure assets are marked as active</li>
                  <li>• Review conflicting trip assignments</li>
                  <li>• Consider manually assigning these trips</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center bg-green-50 border border-green-200 rounded-lg">
              <div className="flex justify-center mb-4">
                <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-green-900 mb-2">
                Perfect! All Bookings Assigned
              </h3>
              <p className="text-green-700">
                The auto-scheduler successfully assigned all trips without any conflicts.
              </p>
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AutoSchedulerWarningsDialog;
